/**
 * Log a failed backend mutation/request at the level its HTTP status warrants.
 *
 * A **4xx** is a validation / user-blocked outcome — expected-bad, not a system fault
 * (e.g. a 400 "Bookings must be in the future", a 409 name conflict) — so it logs as
 * `warn`. A **5xx**, or a transport failure with **no status** (offline / CORS / DNS /
 * abort), is a genuine fault and logs as `error`. This mirrors the `$api`
 * `onResponseError` gate (app/plugins/01.api.ts) so store-level failure logs don't page
 * on ordinary user errors.
 *
 * The `Error` is passed positionally so frogger's serializeError lifts name/message/stack.
 * `statusCode` is derived from the error and merged into `ctx`, so callers can drop their
 * own extraction.
 */
export function logApiFailure(
	message: string,
	err: unknown,
	context?: Record<string, unknown>,
): void {
	const statusCode =
		err && typeof err === 'object' && 'statusCode' in err
			? (err as { statusCode?: number }).statusCode
			: undefined;

	const ctx = { statusCode, ...context };

	// 4xx = expected/user-blocked -> warn; 5xx or no status (never completed) -> error.
	if (typeof statusCode === 'number' && statusCode >= 400 && statusCode < 500) {
		frogger.warn(message, err, ctx);
	} else {
		frogger.error(message, err, ctx);
	}
}
